import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { FirEpDrilldownService } from './fir-ep-drilldown.service';

@Component({
    selector: 'gtw-fir-ep-drilldown',
    templateUrl: './fir-ep-drilldown.component.html',
    styleUrls: ['./fir-ep-drilldown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirEpDrilldownComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly contextSubs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  filterParams: any = {};
  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};

  modalTitle = 'E & P Adjustments';
  minimise = false;
  loading = false;
  displayTable = false;
  tableHeader: string[] = [];
  initialEpDrilldownScreenData: any[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private firEpDrilldownService: FirEpDrilldownService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? '',
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.refreshData();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  refreshData(): void {
    if (this.isLoading) {
      return;
    }
    const taxYear = this.filterParams?.tax_year || this.globalService?.tax_year;
    const scenario = this.filterParams?.scenario || this.globalService?.scenario;
    const filingKey = this.filterParams?.filing_key || this.globalService?.filing_key;
    const jcdKey = this.rowData?.JCD_KEY || this.filterParams?.jcd_key || this.globalService?.jcd_key || 250;
    const firHoLeid = JSON.stringify({
      HO_LEID: this.rowData?.HO_LEID,
      HO_CDR_NO: this.rowData?.HO_CDR_NO,
      HO_REPORTING_PERIOD: this.rowData?.HO_REPORTING_PERIOD,
    });

    const requestParams = {
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: jcdKey,
      screen_key: this.globalService?.screen_key,
      filing_key: filingKey,
      fir_ho_leid: firHoLeid,
    };

    this.loading = true;
    this.displayTable = false;
    this.isLoading = true;
    this.cdr.detectChanges();

    this.firEpDrilldownService.getEpDrilldownScreenData(this.GENERAL_CONFIG.base_url, requestParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
          this.initialEpDrilldownScreenData = data.jsonObject;
          this.loadEpDrilldownTableData();
          this.displayTable = this.tableHeader.length > 0;
        } else {
          this.initialEpDrilldownScreenData = [];
          this.tableHeader = [];
          this.displayTable = false;
        }
        this.loading = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.loading = false;
        this.isLoading = false;
        this.displayTable = false;
        this.cdr.detectChanges();
      }
    );
  }

  dispChildColumn(row: any): void {
    row.SHOW_DATA = !row.SHOW_DATA;
    this.initialEpDrilldownScreenData = this.initialEpDrilldownScreenData.map((item: any) => {
      if (item?.DISP_ACCT === row?.DISP_ACCT && item?.ACCOUNT_KEY === 0) {
        return { ...item, SHOW_DATA: row.SHOW_DATA };
      }
      return item;
    });
    this.cdr.detectChanges();
  }

  checkNumber(value: any): boolean {
    return typeof value === 'number';
  }

  get visibleRows(): any[] {
    return this.initialEpDrilldownScreenData.filter((row: any) => {
      const rowType = row?.ROW_TYPE;
      return rowType === 'EP' || rowType === 'GAAP' || rowType === 'TOTAL' || rowType === 'GAAP_TOTAL' || (rowType === 'EP_Child' && row?.SHOW_DATA);
    });
  }

  private loadEpDrilldownTableData(): void {
    if (!this.initialEpDrilldownScreenData?.length) {
      this.tableHeader = [];
      return;
    }

    this.tableHeader = [];
    Object.keys(this.initialEpDrilldownScreenData[0]).forEach((key: string) => {
      if (key === 'ACCT_DESC') {
        this.tableHeader.push('Account Description');
      } else if (key === 'DISP_ACCT') {
        this.tableHeader.push('Account');
      } else if (key === 'TOTAL') {
        this.tableHeader.push('Current Year Amount');
      } else {
        this.tableHeader.push(key);
      }
    });

    this.initialEpDrilldownScreenData = this.initialEpDrilldownScreenData.map((row: any) => {
      const updated = { ...row };
      updated['Account Description'] = updated.ACCT_DESC;
      updated.Account = updated.DISP_ACCT;
      updated['Current Year Amount'] = updated.TOTAL;
      if (updated['Account Description'] === 'Earnings_and_Profits') {
        updated['Account Description'] = 'Earnings and Profits';
      }
      if (updated['Account Description'] === 'Total_EP_Adjustments') {
        updated['Account Description'] = 'Total EP Adjustments';
      }
      if (updated.ROW_TYPE === 'EP' || updated.ROW_TYPE === 'EP_Child') {
        updated.SHOW_DATA = false;
      }
      return updated;
    });
  }
}
